import React, { useState, useEffect, useCallback } from 'react';
import { chatsApi, ChatType } from '../../api/chats';
import { profilesApi } from '../../api/profiles';
import HrnbModal from '../controls/HrnbModal';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbSearch from '../controls/HrnbSearch';
import '../../css/chats/ForwardMessageModal.css';

/**
 * Модальное окно пересылки сообщений
 * Показывает список чатов текущего пользователя для выбора получателя
 */
export default function ForwardMessageModal({ currentChatId, onClose, onForward, loading = false }) {
    const [chats, setChats] = useState([]);
    const [filteredChats, setFilteredChats] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loadingChats, setLoadingChats] = useState(true);
    const [error, setError] = useState(null);
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [profileCache, setProfileCache] = useState({});
    const [comment, setComment] = useState('');

    const fetchChats = useCallback(async () => {
        setLoadingChats(true);
        setError(null);
        try {
            const list = await chatsApi.listChats();
            // Исключаем текущий чат
            const otherChats = list.filter(c => c.id !== currentChatId);
            setChats(otherChats);
            setFilteredChats(otherChats);

            // Загружаем профили для DIRECT чатов
            const directChats = otherChats.filter(c => c.chatType === ChatType.DIRECT);
            const profileIds = directChats
                .flatMap(c => c.participantIds || c.participants || [])
                .filter(Boolean);

            const uniqueProfileIds = [...new Set(profileIds)];
            if (uniqueProfileIds.length > 0) {
                const cache = {};
                await Promise.all(
                    uniqueProfileIds.map(async (pid) => {
                        try {
                            const resp = await profilesApi.get(pid);
                            const data = resp.data || resp;
                            cache[pid] = data;
                        } catch (e) {
                            // игнорируем ошибки загрузки профилей
                        }
                    })
                );
                setProfileCache(cache);
            }
        } catch (err) {
            setError(err.message || 'Не удалось загрузить чаты');
        } finally {
            setLoadingChats(false);
        }
    }, [currentChatId]);

    useEffect(() => {
        fetchChats();
    }, [fetchChats]);

    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredChats(chats);
            return;
        }
        const q = searchQuery.toLowerCase();
        const filtered = chats.filter(chat => {
            const title = (chat.title || '').toLowerCase();
            if (title.includes(q)) return true;
            // Для DIRECT чатов ищем по имени собеседника
            if (chat.chatType === ChatType.DIRECT) {
                const pids = chat.participantIds || chat.participants || [];
                for (const pid of pids) {
                    const profile = profileCache[pid];
                    if (profile?.displayName?.toLowerCase().includes(q)) return true;
                }
            }
            return false;
        });
        setFilteredChats(filtered);
    }, [searchQuery, chats, profileCache]);

    const getChatDisplayName = (chat) => {
        if (chat.chatType !== ChatType.DIRECT) {
            return chat.title || 'Групповой чат';
        }
        const pids = chat.participantIds || chat.participants || [];
        for (const pid of pids) {
            const profile = profileCache[pid];
            if (profile?.displayName) return profile.displayName;
        }
        return 'Личный чат';
    };

    const getChatIcon = (chat) => {
        if (chat.chatType === ChatType.DIRECT) return '👤';
        if (chat.chatType === ChatType.BATTLE_SUPPORT) return '⚔';
        if (chat.chatType === ChatType.BATTLE_GROUP) return '⚔';
        if (chat.chatType === ChatType.BATTLE_LIVE) return '⚔';
        if (chat.chatType === ChatType.BATTLE_PARTICIPANTS) return '⚔';
        return '👥';
    };

    const getChatTypeLabel = (chat) => {
        if (chat.chatType === ChatType.DIRECT) return 'Личный чат';
        if (chat.chatType === ChatType.BATTLE_SUPPORT) return 'Поддержка баттла';
        if (chat.chatType === ChatType.BATTLE_GROUP) return 'Чат баттла';
        if (chat.chatType === ChatType.BATTLE_LIVE) return 'Live-чат баттла';
        if (chat.chatType === ChatType.BATTLE_PARTICIPANTS) return 'Чат участников';
        return 'Групповой чат';
    };

    const handleForward = () => {
        if (selectedChatId) {
            onForward(selectedChatId, comment);
        }
    };

    const handleChatClick = (chatId) => {
        setSelectedChatId(chatId);
    };

    return (
        <HrnbModal
            open={true}
            onClose={onClose}
            title="Переслать сообщение"
        >
            <div className="forward-modal">
                {loadingChats ? (
                    <div className="forward-modal__loading">
                        <HrnbLoader />
                    </div>
                ) : (
                    <>
                        {/* Комментарий к пересылаемому сообщению */}
                        <div className="forward-modal__comment">
                            <textarea
                                className="forward-modal__comment-input"
                                placeholder="Комментарий (необязательно)..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                rows={2}
                                maxLength={500}
                            />
                        </div>

                        <div className="forward-modal__search">
                            <HrnbSearch
                                placeholder="Поиск чата..."
                                value={searchQuery}
                                onChange={setSearchQuery}
                            />
                        </div>

                        {error && (
                            <HrnbAlert variant="danger" onClose={() => setError(null)}>
                                {error}
                            </HrnbAlert>
                        )}

                        <div className="forward-modal__list">
                            {filteredChats.length === 0 ? (
                                <div className="forward-modal__empty">
                                    {searchQuery ? 'Чаты не найдены' : 'Нет доступных чатов для пересылки'}
                                </div>
                            ) : (
                                filteredChats.map(chat => (
                                    <div
                                        key={chat.id}
                                        className={`forward-modal__item ${selectedChatId === chat.id ? 'forward-modal__item--selected' : ''}`}
                                        onClick={() => handleChatClick(chat.id)}
                                    >
                                        <div className="forward-modal__item-icon">
                                            {getChatIcon(chat)}
                                        </div>
                                        <div className="forward-modal__item-info">
                                            <div className="forward-modal__item-name">
                                                {getChatDisplayName(chat)}
                                            </div>
                                            <div className="forward-modal__item-type">
                                                {getChatTypeLabel(chat)}
                                            </div>
                                        </div>
                                        <div className="forward-modal__item-check" />
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}

                <div className="forward-modal__actions">
                    <HrnbButton variant="secondary" onClick={onClose} disabled={loading}>
                        Отмена
                    </HrnbButton>
                    <HrnbButton
                        onClick={handleForward}
                        disabled={!selectedChatId || loading}
                    >
                        {loading ? <HrnbLoader size="sm" /> : 'Переслать'}
                    </HrnbButton>
                </div>
            </div>
        </HrnbModal>
    );
}
